/* eslint-disable no-return-assign */
import React, { useRef, useState } from 'react';
import { hasValue } from 'swipeclock-frontend';
import { useTheme } from '@mui/material/styles';
import { CSSTransition } from 'react-transition-group';
import {
  ErrorTransitionWrapper,
  InputErrorText,
  InputHintText,
} from './Styles';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import Tooltip from '@mui/material/Tooltip';

/**
 * An uncontrolled text field component that renders a label, input, and optional error and hint text.
 * @param {Object} props - The props object.
 * @param {string} [props.error] - The error message to display for the text field.
 * @param {boolean} [props.gutter] - Whether to display a gutter below the text field.
 * @param {string} [props.hint] - The hint text to display for the text field.
 * @param {string} props.id - The ID of the text field.
 * @param {object} props.inputProps - Additional props to pass to the base input component
 * @param {string} props.label - The label for the text field.
 * @param {function} [props.onBlur] - The blur event handler for the text field.
 * @param {function} [props.onChange] - The change event handler for the text field.
 * @param {boolean} [props.required] - Whether the text field is required.
 * @param {function} props.validationRef - The ref for keeping track of wizard/form validation.
 * @returns {JSX.Element} - The text field component.
 */
export default function PasswordTextfield({
  animateError = true,
  error,
  gutter,
  hint,
  id,
  inputProps,
  label,
  onBlur: handleBlur,
  onChange: handleChange,
  required: isRequired,
  validationRef,
  ...props
}) {
  const theme = useTheme();
  const errorRef = useRef();
  const [prevError, setPrevError] = useState(false);
  const [showError, setShowError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  // Caps lock state used to notify the user if caps lock is on while typing in their password
  const [capsLockOn, setCapsLockOn] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  // We need to adjust some state. Specifically, we need to know when the error
  // prop changes. This is because we want to animate the error text in and out.
  // Storing information from the previous render is a good way to do this.
  // We start then by updating the previous error state to the current error state.
  // When they are not the same. Then we do another if statement to check if the
  // error is true and if it wasn't true before. If so,
  // we set the show error state to true.
  if (!!error !== prevError) {
    setPrevError(!!error);
  }

  if (!!error && !!error !== prevError) {
    setShowError(true);
  }

  // check to see if caps lock has been turned on
  // possibly add a throttle to this function
  const verifyCapsLock = (event) => {
    // verify that the target has a value prior to performing the check because
    // we do not know the initial state of the caps lock key on page load
    if (event.getModifierState('CapsLock') && hasValue(event.target?.value)) {
      if (event.key === 'CapsLock') {
        setCapsLockOn((prev) => !prev);
      } else {
        setCapsLockOn(true);
      }
    } else {
      setCapsLockOn(false);
    }
  };

  return (
    <CSSTransition
      in={!!error}
      timeout={theme.transitions.duration.shorter}
      classNames="error-text"
      nodeRef={errorRef}
      onExited={() => {
        setShowError(false);
      }}
    >
      <FormControl fullWidth {...props}>
        <TextField
          fullWidth
          id={id}
          inputProps={{
            maxLength: props?.maxLength > 0 ? props.maxLength : null,
            onKeyUp: verifyCapsLock,
            ...inputProps,
          }}
          inputRef={(el) => (validationRef.current[id] = el)}
          label={label}
          name={id}
          onBlur={(event) => {
            event.target.value = event.target.value.trim();
            handleBlur(event);
          }}
          onChange={handleChange}
          required={isRequired}
          variant="outlined"
          type={showPassword ? 'text' : 'password'}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {capsLockOn && (
                  <Tooltip title="Caps Lock is enabled" placement="top">
                    <KeyboardCapslockIcon
                      sx={{ color: theme.palette.error.main }}
                    />
                  </Tooltip>
                )}
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...props}
        />
        {/* If no animation is needed this will show an error right away. */}
        {!animateError && error && <InputErrorText>{error}</InputErrorText>}
        {animateError && showError && (
          <ErrorTransitionWrapper ref={errorRef}>
            <InputErrorText>{error}</InputErrorText>
          </ErrorTransitionWrapper>
        )}
        {hint && !hasValue(error) && <InputHintText>{hint}</InputHintText>}
        {/* Propbably don't want this if we are animating errors. */}
        {gutter && !hasValue(error) && !hasValue(hint) && (
          <InputHintText>&nbsp;</InputHintText>
        )}
      </FormControl>
    </CSSTransition>
  );
}

PasswordTextfield.propTypes = {
  animateError: PropTypes.bool,
  error: PropTypes.string,
  gutter: PropTypes.bool,
  hint: PropTypes.string,
  id: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  label: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  validationRef: PropTypes.any,
};
